import React from "react";
import { useConsent } from "../context/ConsentContext";
import { useI18n } from "../i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Shield, SlidersHorizontal } from "lucide-react";

export function ConsentBanner() {
  const { hasChoice, acceptAll, refuseAll, openCenter } = useConsent();
  const { t } = useI18n();
  if (hasChoice) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 xl:bottom-4 xl:left-4 xl:right-auto xl:max-w-md"
      role="dialog" aria-label={t("consent.title")} data-testid="consent-banner">
      <div className="mx-auto max-w-6xl px-4 pb-20 xl:p-0">
        <div className="rounded-2xl border border-black/10 bg-white p-6 shadow-xl">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#2A9D8F]" />
            <h2 className="font-display text-xl">{t("consent.title")}</h2>
          </div>
          <p className="mt-3 text-sm text-[#5C6370]">{t("consent.body")}</p>
          <div className="mt-5 grid gap-2 sm:grid-cols-3">
            <Button variant="outline" onClick={refuseAll} data-testid="consent-refuse"
              className="rounded-full min-h-[44px] border-[#2A9D8F]/40 text-[#2D3142]">
              {t("consent.refuse")}
            </Button>
            <Button variant="outline" onClick={openCenter} data-testid="consent-customize"
              className="rounded-full min-h-[44px] border-[#2A9D8F]/40 text-[#2D3142]">
              {t("consent.customize")}
            </Button>
            <Button onClick={acceptAll} data-testid="consent-accept-all"
              className="rounded-full min-h-[44px] bg-[#2A9D8F] hover:bg-[#21867A]">
              {t("consent.acceptAll")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PreferenceCenter() {
  const { centerOpen, closeCenter, savePrefs, functionalAllowed } = useConsent();
  const { t } = useI18n();
  const [functional, setFunctional] = React.useState(functionalAllowed);

  React.useEffect(() => { setFunctional(functionalAllowed); }, [functionalAllowed, centerOpen]);

  return (
    <Dialog open={centerOpen} onOpenChange={(o) => !o && closeCenter()}>
      <DialogContent className="max-w-lg" data-testid="preference-center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <SlidersHorizontal className="h-5 w-5 text-[#2A9D8F]" /> {t("consent.centerTitle")}
          </DialogTitle>
          <DialogDescription>{t("consent.body")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-black/10 bg-[#F9F6F0] p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold">{t("consent.necessary")}</p>
                <p className="text-sm text-[#5C6370]">{t("consent.necessaryDesc")}</p>
              </div>
              <span className="rounded-full bg-[#9CAF88] px-3 py-1 text-xs font-semibold text-[#1E293B]">
                {t("consent.alwaysOn")}
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-black/10 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold">{t("consent.functional")}</p>
                <p className="text-sm text-[#5C6370]">{t("consent.functionalDesc")}</p>
              </div>
              <Switch checked={functional} onCheckedChange={setFunctional} data-testid="consent-functional-switch"
                aria-label={t("consent.functional")} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => savePrefs(functional)} data-testid="consent-save"
              className="rounded-full min-h-[44px] bg-[#2A9D8F] hover:bg-[#21867A]">
              {t("consent.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
